import type { FoundryV2RuntimeReleaseRecord } from "../api/foundryV2Client";

function verdictTone(verdict: "pass" | "watch" | "fail"): string {
  if (verdict === "pass") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (verdict === "watch") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-red-200 bg-red-50 text-red-700";
}

function statusTone(status: "ready" | "watch" | "blocked"): string {
  if (status === "ready") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "watch") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-red-200 bg-red-50 text-red-700";
}

function controlTone(verdict: "pass" | "watch" | "fail"): string {
  return verdictTone(verdict);
}

type Props = {
  runtimeRelease: FoundryV2RuntimeReleaseRecord | null;
  isLoading: boolean;
  error: string | null;
};

export function FoundryRuntimeReleasePanel({ runtimeRelease, isLoading, error }: Props): JSX.Element {
  const summary = runtimeRelease?.summary;

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f7fbff_100%)] p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)]"
      data-testid="foundry-runtime-release-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
            Runtime &amp; Release
          </div>
          <h3 className="mt-2 text-xl font-semibold text-slate-950">
            Runtime, deployment, rollback, support, and attestation are explicit before promotion
          </h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            This surface replaces raw artifact reading with policy-pack truth. Promotion is allowed only when runtime,
            deployment, rollback, support, and compliance are materially closed.
          </p>
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      {isLoading && !runtimeRelease ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Loading runtime and release posture...
        </div>
      ) : null}

      {runtimeRelease && summary ? (
        <>
          <div className="mt-5 grid gap-3 xl:grid-cols-[1.1fr_0.9fr_0.9fr]">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="flex items-center gap-2">
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${statusTone(summary.status)}`}>
                  {summary.status}
                </span>
                <span className="text-xs text-slate-500">
                  {summary.policyPackCount} packs Â· {summary.blockingPackCount} blocking Â· {summary.watchPackCount} watch
                </span>
              </div>
              <div className="mt-3 text-sm text-slate-700">{summary.headline}</div>
              <div className="mt-2 text-xs text-slate-500">{summary.rationale}</div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Founder view</div>
              <ul className="mt-3 space-y-2 text-xs text-slate-600">
                {runtimeRelease.readableViews.founder.map((item) => (
                  <li key={item} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Artifacts</div>
              <div className="mt-3 space-y-2 text-xs">
                <a className="block font-medium text-blue-700 underline" href={runtimeRelease.artifactUrls.runtimeContract}>
                  runtime_contract.v2.json
                </a>
                <a className="block font-medium text-blue-700 underline" href={runtimeRelease.artifactUrls.policyPacks}>
                  enterprise_policy_packs.v1.json
                </a>
                <a className="block font-medium text-blue-700 underline" href={runtimeRelease.artifactUrls.deploymentContract}>
                  deployment_contract.v2.json
                </a>
                <a className="block font-medium text-blue-700 underline" href={runtimeRelease.artifactUrls.rollbackPack}>
                  rollback_pack.v2.json
                </a>
                <a className="block font-medium text-blue-700 underline" href={runtimeRelease.artifactUrls.supportRunbook}>
                  support_runbook.v2.md
                </a>
                <a className="block font-medium text-blue-700 underline" href={runtimeRelease.artifactUrls.releaseAttestation}>
                  release_attestation.v2.json
                </a>
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4" data-testid="foundry-runtime-profiles-panel">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Runtime profiles</div>
                  <div className="mt-1 text-xs text-slate-600">
                    Foundry now makes the customer runtime explicit instead of leaving Jarvis to infer it from stack fragments.
                  </div>
                </div>
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-slate-700">
                  {runtimeRelease.stackRecommendation.rationale}
                </div>
              </div>
              <div className="mt-4 grid gap-3 xl:grid-cols-2">
                {runtimeRelease.runtimeProfiles.map((profile) => (
                  <div
                    key={profile.id}
                    className={`rounded-2xl border p-4 ${
                      profile.selected
                        ? "border-blue-300 bg-blue-50/60"
                        : profile.recommended
                          ? "border-emerald-200 bg-emerald-50/50"
                          : "border-slate-200 bg-slate-50/70"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{profile.label}</div>
                        <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                          {profile.operatingModel}
                        </div>
                      </div>
                      <div className="flex flex-wrap justify-end gap-2">
                        {profile.selected ? (
                          <span className="rounded-full border border-blue-300 bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                            selected
                          </span>
                        ) : null}
                        {profile.recommended ? (
                          <span className="rounded-full border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                            recommended
                          </span>
                        ) : null}
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${verdictTone(profile.verdict)}`}>
                          {profile.verdict}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-slate-600">{profile.summary}</div>
                    <div className="mt-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-[11px] text-slate-600">
                      {profile.stackHint}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-500">
                      {profile.goodFitFor.map((item) => (
                        <span key={`${profile.id}-${item}`} className="rounded-full border border-slate-200 bg-white px-2 py-1">
                          {item}
                        </span>
                      ))}
                    </div>
                    <div className="mt-3 grid gap-2">
                      {profile.controls.map((control) => (
                        <div key={`${profile.id}-${control.id}`} className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                              {control.label}
                            </div>
                            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${controlTone(control.status)}`}>
                              {control.status}
                            </span>
                          </div>
                          <div className="mt-1 text-xs text-slate-600">{control.summary}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 space-y-2 text-xs text-slate-600">
                      {profile.implications.map((implication) => (
                        <div key={`${profile.id}-${implication}`} className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                          {implication}
                        </div>
                      ))}
                      {profile.blockerReasons.map((reason) => (
                        <div key={`${profile.id}-${reason}`} className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-red-800">
                          {reason}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Stack recommendation</div>
              <div className="mt-3 space-y-3">
                {runtimeRelease.stackRecommendation.comparedOptions.map((option) => (
                  <div key={option.profileId} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-600">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-semibold text-slate-900">{option.label}</div>
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${verdictTone(option.verdict)}`}>
                        {option.verdict}
                      </span>
                    </div>
                    <div className="mt-2">{option.summary}</div>
                    <div className="mt-2 text-[11px] text-slate-500">{option.implicationHeadline}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Policy packs</div>
              <div className="mt-3 grid gap-3 lg:grid-cols-2">
                {runtimeRelease.policyPacks.map((pack) => (
                  <div key={pack.id} className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{pack.label}</div>
                        <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                          {pack.category}
                        </div>
                      </div>
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${verdictTone(pack.verdict)}`}>
                        {pack.verdict}
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-slate-600">{pack.summary}</div>
                    {pack.controls.length ? (
                      <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-500">
                        {pack.controls.slice(0, 4).map((control) => (
                          <span key={`${pack.id}-control-${control}`} className="rounded-full border border-slate-200 bg-white px-2 py-1">
                            {control}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-500">
                      {pack.evidenceRefs.map((ref) => (
                        <span key={`${pack.id}-${ref}`} className="rounded-full border border-slate-200 bg-white px-2 py-1">
                          {ref}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Blocking truth</div>
              <div className="mt-3 space-y-3 text-xs text-slate-600">
                {runtimeRelease.blockers.map((blocker) => (
                  <div
                    key={blocker.id}
                    className={`rounded-xl border px-3 py-3 ${
                      blocker.severity === "blocking"
                        ? "border-red-200 bg-red-50 text-red-800"
                        : "border-amber-200 bg-amber-50 text-amber-800"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-semibold">{blocker.packId}</div>
                      <span className="rounded-full border border-current/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                        {blocker.severity}
                      </span>
                    </div>
                    <div className="mt-2">{blocker.reason}</div>
                  </div>
                ))}
                {!runtimeRelease.blockers.length ? (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-emerald-800">
                    No runtime or release blocker remains.
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
